import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
} from '@xyflow/react'
import { produce } from 'immer'
import { nanoid } from 'nanoid'
import { create } from 'zustand'
import { BuilderNodeType } from '@/features/flow/_builder/components/blocks/types'

export interface NodeValidationError {
  nodeId: string
  nodeType: string
  field: string
  message: string
}

export interface FlowValidationResult {
  isValid: boolean
  errors: string[]
  nodeErrors: NodeValidationError[]
}

interface State {
  nodes: Node[]
  edges: Edge[]
}

interface Actions {
  actions: {
    saveWorkflow: () => IFlowState['workflow']
    setWorkflow: (workflow: IFlowState['workflow']) => void
    nodes: {
      onNodesChange: (changes: NodeChange[]) => void
      setNodes: (nodes: Node[]) => void
      deleteNode: (node: Node) => void
    }
    edges: {
      onEdgesChange: (changes: EdgeChange[]) => void
      onConnect: (connection: Connection) => void
      setEdges: (edges: Edge[]) => void
      deleteEdge: (edge: Edge) => void
    }
    sidebar: {
      setActivePanel: (
        panel:
          | 'node-properties'
          | 'available-nodes'
          | 'validation-errors'
          | 'none'
      ) => void
      setSidebarOpen: (isOpen: boolean) => void
      showNodePropertiesOf: (node: {
        id: string
        type: BuilderNodeType
      }) => void
      panels: {
        nodeProperties: {
          setSelectedNode: (
            node: { id: string; type: BuilderNodeType } | undefined | null
          ) => void
        }
      }
    }
    validation: {
      setValidationResult: (result: FlowValidationResult | null) => void
      clearValidationErrors: () => void
      hasErrors: () => boolean
      getErrorCount: () => number
      getNodeErrors: (nodeId: string) => NodeValidationError[]
    }
  }
}

export interface IFlowState {
  workflow: {
    id: number
    name: string
  } & State
  sidebar: {
    active: 'node-properties' | 'available-nodes' | 'validation-errors' | 'none'
    isOpen: boolean
    panels: {
      nodeProperties: {
        selectedNode?: { id: string; type: BuilderNodeType } | null | undefined
      }
    }
  }
  validationResult: FlowValidationResult | null
  actions: Actions['actions']
}

const getSidebarOpenFromStorage = (): boolean => {
  if (typeof window === 'undefined') return true
  const stored = localStorage.getItem('wazai-flow-sidebar-open')
  return stored !== null ? JSON.parse(stored) : true
}

const setSidebarOpenToStorage = (isOpen: boolean): void => {
  if (typeof window === 'undefined') return
  localStorage.setItem('wazai-flow-sidebar-open', JSON.stringify(isOpen))
}

export const useFlowStore = create<IFlowState>()((set, get) => ({
  workflow: {
    id: 0,
    name: '',
    edges: [],
    nodes: [],
  },
  sidebar: {
    active: 'available-nodes',
    isOpen: getSidebarOpenFromStorage(),
    panels: {
      nodeProperties: {
        selectedNode: null,
      },
    },
  },
  validationResult: null,
  actions: {
    saveWorkflow: () => {
      const { workflow } = get()
      set({ workflow })
      return workflow
    },
    setWorkflow: (workflow: Omit<IFlowState['workflow'], 'sidebar'>) => {
      set((state) => ({
        workflow: {
          ...state.workflow,
          ...workflow,
        },
      }))
    },
    sidebar: {
      setActivePanel: (
        panel:
          | 'node-properties'
          | 'available-nodes'
          | 'validation-errors'
          | 'none'
      ) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            active: panel,
            panels: {
              ...state.sidebar.panels,
              nodeProperties: {
                ...state.sidebar.panels.nodeProperties,
              },
            },
          },
        }))
      },
      setSidebarOpen: (isOpen: boolean) => {
        setSidebarOpenToStorage(isOpen)
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen,
          },
        }))
      },
      showNodePropertiesOf: (node: { id: string; type: BuilderNodeType }) => {
        setSidebarOpenToStorage(true)
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen: true,
            active: 'node-properties',
            panels: {
              ...state.sidebar.panels,
              nodeProperties: {
                ...state.sidebar.panels.nodeProperties,
                selectedNode: node,
              },
            },
          },
        }))
      },
      panels: {
        nodeProperties: {
          setSelectedNode: (
            node: { id: string; type: BuilderNodeType } | undefined | null
          ) =>
            set((state) => ({
              sidebar: {
                ...state.sidebar,
                active: state.sidebar.active,
                panels: {
                  ...state.sidebar.panels,
                  nodeProperties: {
                    ...state.sidebar.panels.nodeProperties,
                    selectedNode: node,
                  },
                },
              },
            })),
        },
      },
    },
    nodes: {
      onNodesChange: (changes) => {
        set((state) =>
          produce(state, (draft) => {
            const updatedNodes = applyNodeChanges(changes, draft.workflow.nodes)

            draft.workflow.nodes = updatedNodes
          })
        )
      },
      setNodes: (nodes) => {
        set({ workflow: { ...get().workflow, nodes } })
      },
      deleteNode: (node: Node) => {
        set((state) => ({
          workflow: {
            ...state.workflow,
            nodes: state.workflow.nodes.filter((n) => n.id !== node.id),
          },
        }))
      },
    },
    edges: {
      onEdgesChange: (changes) => {
        set((state) =>
          produce(state, (draft) => {
            const updatedEdges = applyEdgeChanges(changes, draft.workflow.edges)

            draft.workflow.edges = updatedEdges
          })
        )
      },
      onConnect: (connection) => {
        const edge = { ...connection, id: nanoid(), type: 'deletable' } as Edge
        set({
          workflow: {
            ...get().workflow,
            edges: addEdge(edge, get().workflow.edges),
          },
        })
      },
      setEdges: (edges) => {
        set({ workflow: { ...get().workflow, edges } })
      },
      deleteEdge: (edge: Edge) => {
        set((state) => ({
          workflow: {
            ...state.workflow,
            edges: state.workflow.edges.filter((e) => e.id !== edge.id),
          },
        }))
      },
    },
    validation: {
      setValidationResult: (result) => set({ validationResult: result }),

      clearValidationErrors: () => set({ validationResult: null }),

      hasErrors: () => {
        const { validationResult } = get()
        return validationResult ? !validationResult.isValid : false
      },

      getErrorCount: () => {
        const { validationResult } = get()
        if (!validationResult) return 0
        return (
          validationResult.errors.length + validationResult.nodeErrors.length
        )
      },

      getNodeErrors: (nodeId: string) => {
        const { validationResult } = get()
        if (!validationResult) return []
        return validationResult.nodeErrors.filter(
          (error) => error.nodeId === nodeId
        )
      },
    },
  },
}))
